/**
 * Stripe Configuration
 * Centralized Stripe settings for custom checkout
 */

function resolvePublishableStripeKey(raw: string): string {
  const value = raw.trim();
  if (!value) return "";
  if (value.startsWith("sk_") || value.startsWith("cs_")) {
    console.warn(
      "[stripe] VITE_STRIPE_PUBLISHABLE_KEY must be the publishable key (pk_test_… or pk_live_…). " +
        "Do not put secret keys (sk_…) or Checkout Session client secrets (cs_…) in the frontend env.",
    );
    return "";
  }
  if (import.meta.env.DEV && !value.startsWith("pk_")) {
    console.warn(
      "[stripe] VITE_STRIPE_PUBLISHABLE_KEY should start with pk_test_ or pk_live_.",
    );
  }
  return value;
}

// Stripe publishable key from environment (pk_* only; never sk_* or cs_*)
export const STRIPE_PUBLISHABLE_KEY = resolvePublishableStripeKey(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "",
);

// API endpoint for creating payment intents
export const CREATE_PAYMENT_INTENT_ENDPOINT = "/api/create-payment-intent";

// Pack pricing configuration
export interface PackPricing {
  id: string;
  name: string;
  nameEs: string; // Spanish name
  description: string;
  descriptionEs: string; // Spanish description
  amount: number; // Amount in cents (default/fallback)
  currency: string; // Default currency
  thumbnail?: string;
  // Country-specific pricing (overrides default amount)
  priceByCountry?: {
    [countryCode: string]: {
      amount: number; // Amount in cents
      currency: string;
    };
  };
}

// =============================================================================
// PRODUCT CATALOG
// =============================================================================

/**
 * Available Products/Packs
 * Each product has default pricing and optional country-specific pricing
 */
export const PACKS: Record<string, PackPricing> = {
  // Invitación Canal / Channel Invite - NEW PRODUCT
  channel_invite: {
    id: "channel_invite",
    name: "Channel Invite",
    nameEs: "Invitación Canal",
    description: "Exclusive access to premium channel",
    descriptionEs: "Acceso exclusivo al canal premium",
    amount: 2999, // $29.99 USD (default)
    currency: "usd",
    priceByCountry: {
      // High price countries ($29.99)
      US: { amount: 2999, currency: "usd" },
      ES: { amount: 2999, currency: "usd" },
      DE: { amount: 2999, currency: "usd" },
      FR: { amount: 2999, currency: "usd" },
      MX: { amount: 2999, currency: "usd" },
      CO: { amount: 2999, currency: "usd" },
      PE: { amount: 2999, currency: "usd" },
      PY: { amount: 2999, currency: "usd" },
      BO: { amount: 2999, currency: "usd" },

      // Lower price countries
      CL: { amount: 1999, currency: "usd" }, // $19.99
      BR: { amount: 1999, currency: "usd" }, // $19.99
      UY: { amount: 1999, currency: "usd" }, // $19.99

      // LATAM fallback (Worker/env sin país específico, e.g. Chile) — mismo precio que CL para que web = /invitacion-privada
      LATAM: { amount: 1999, currency: "usd" }, // $19.99

      // Argentina special pricing
      AR: { amount: 1499, currency: "usd" }, // $14.99
    },
  },

  // Telegram Groups Premium
  telegram_groups: {
    id: "telegram_groups",
    name: "Telegram Groups Premium",
    nameEs: "Grupos Premium de Telegram",
    description: "Access to exclusive Telegram groups",
    descriptionEs: "Acceso a grupos exclusivos de Telegram",
    amount: 2499, // $29.99 USD (default)
    currency: "usd",
    priceByCountry: {
      // High price countries ($29.99)
      US: { amount: 1999, currency: "usd" },
      ES: { amount: 1999, currency: "usd" },
      DE: { amount: 2999, currency: "usd" },
      FR: { amount: 1499, currency: "usd" },
      MX: { amount: 1499, currency: "usd" },
      CO: { amount: 1499, currency: "usd" },
      PE: { amount: 1499, currency: "usd" },
      PY: { amount: 1499, currency: "usd" },
      BO: { amount: 1499, currency: "usd" },

      // Lower price countries
      CL: { amount: 1499, currency: "usd" }, // $19.99
      BR: { amount: 1499, currency: "usd" }, // $19.99
      UY: { amount: 1499, currency: "usd" }, // $19.99

      // Argentina special pricing
      AR: { amount: 1499, currency: "usd" }, // $14.99
    },
  },

  // Mejores Amigos Instagram (same price for all countries)
  mejores_amigos: {
    id: "mejores_amigos",
    name: "Instagram Best Friends",
    nameEs: "Mejores Amigos Instagram",
    description: "Exclusive access to Instagram Best Friends",
    descriptionEs: "Acceso exclusivo a Mejores Amigos de Instagram",
    amount: 14999, // $149.99 USD (same for all)
    currency: "usd",
  },

  // Exclusive $39.99 — página /exclusive, video Mux
  exclusive: {
    id: "exclusive",
    name: "Exclusive",
    nameEs: "Exclusive",
    description: "Exclusive content access",
    descriptionEs: "Acceso a contenido exclusivo",
    amount: 3999, // $39.99 USD
    currency: "usd",
  },

  // Sorpresa $99.99 — página /sorpresa, solo botón + fondo Mux playback ID (metadata source: anaafernandez)
  sorpresa: {
    id: "sorpresa",
    name: "Sorpresa",
    nameEs: "Sorpresa",
    description: "Sorpresa",
    descriptionEs: "Sorpresa",
    amount: 9999, // $99.99 USD
    currency: "usd",
  },

  // Talk to Me
  talk: {
    id: "talk",
    name: "Talk to Me",
    nameEs: "Habla Conmigo",
    description: "One-on-one conversation session",
    descriptionEs: "Sesión de conversación uno a uno",
    amount: 4999, // $49.99 USD
    currency: "usd",
  },

  // Group Talk
  group_talk: {
    id: "group_talk",
    name: "Group Talk",
    nameEs: "Charla Grupal",
    description: "Group conversation session",
    descriptionEs: "Sesión de conversación grupal",
    amount: 9999, // $99.99 USD
    currency: "usd",
  },
};

/**
 * Get pack pricing by ID and country
 * Returns country-specific pricing if available, otherwise returns default
 */
export function getPackPricing(
  packId: string,
  countryCode?: string,
): PackPricing | null {
  const pack = PACKS[packId];
  if (!pack) return null;

  // If no country code or no country-specific pricing, return default
  if (!countryCode || !pack.priceByCountry) {
    return pack;
  }

  // Check if country-specific pricing exists
  const countryPricing = pack.priceByCountry[countryCode];
  if (countryPricing) {
    // Return pack with country-specific pricing
    return {
      ...pack,
      amount: countryPricing.amount,
      currency: countryPricing.currency,
    };
  }

  // Return default pricing
  return pack;
}

/**
 * Get localized pack name
 */
export function getPackName(
  packId: string,
  language: "en" | "es" = "en",
): string {
  const pack = PACKS[packId];
  if (!pack) return packId;
  return language === "es" ? pack.nameEs : pack.name;
}

/**
 * Get localized pack description
 */
export function getPackDescription(
  packId: string,
  language: "en" | "es" = "en",
): string {
  const pack = PACKS[packId];
  if (!pack) return "";
  return language === "es" ? pack.descriptionEs : pack.description;
}

/**
 * Get all available packs
 */
export function getAllPacks(): PackPricing[] {
  return Object.values(PACKS);
}

// Brand purple/pink for Stripe Elements (dark theme)
const STRIPE_PRIMARY = "#a855f7";
const STRIPE_BG = "#0a0a0a";
const STRIPE_BG_ELEVATED = "rgba(255, 255, 255, 0.06)";
const STRIPE_BORDER = "rgba(255, 255, 255, 0.15)";
const STRIPE_BORDER_FOCUS = "rgba(168, 85, 247, 0.6)";
const STRIPE_TEXT = "#ffffff";
const STRIPE_TEXT_MUTED = "rgba(255, 255, 255, 0.75)";
const STRIPE_TEXT_PLACEHOLDER = "rgba(255, 255, 255, 0.4)";

/**
 * Stripe Appearance API – dark theme, layout and UX a lo nuestro
 * Ana Fernández branding: dark background, purple primary, readable hierarchy
 */
export const STRIPE_APPEARANCE = {
  theme: "night" as const,
  variables: {
    colorPrimary: STRIPE_PRIMARY,
    colorBackground: STRIPE_BG,
    colorText: STRIPE_TEXT,
    colorDanger: "#ef4444",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif',
    spacingUnit: "6px",
    borderRadius: "12px",
    fontSizeBase: "16px",
    fontWeightNormal: "400",
    fontWeightMedium: "500",
    fontWeightBold: "600",
  },
  rules: {
    ".Block": {
      backgroundColor: STRIPE_BG_ELEVATED,
      border: `1px solid ${STRIPE_BORDER}`,
      borderRadius: "12px",
    },
    ".Input": {
      backgroundColor: STRIPE_BG_ELEVATED,
      border: `1px solid ${STRIPE_BORDER}`,
      color: STRIPE_TEXT,
      padding: "14px 16px",
      fontSize: "17px",
      boxShadow: "none",
      lineHeight: "1.4",
    },
    ".Input:focus": {
      border: `1px solid ${STRIPE_BORDER_FOCUS}`,
      boxShadow: `0 0 0 2px rgba(168, 85, 247, 0.2)`,
      outline: "none",
    },
    ".Input::placeholder": {
      color: STRIPE_TEXT_PLACEHOLDER,
    },
    ".Label": {
      color: STRIPE_TEXT_MUTED,
      fontSize: "15px",
      fontWeight: "500",
      marginBottom: "8px",
      letterSpacing: "-0.01em",
    },
    ".Tab": {
      backgroundColor: STRIPE_BG_ELEVATED,
      border: `1px solid ${STRIPE_BORDER}`,
      color: STRIPE_TEXT_MUTED,
      padding: "14px 16px",
      fontSize: "17px",
    },
    ".Tab:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.08)",
      color: STRIPE_TEXT,
    },
    ".Tab--selected": {
      backgroundColor: "rgba(168, 85, 247, 0.15)",
      border: `1px solid ${STRIPE_PRIMARY}`,
      color: STRIPE_TEXT,
      fontWeight: "500",
      boxShadow: `0 0 0 1px ${STRIPE_PRIMARY}`,
    },
    ".PickerItem--selected": {
      backgroundColor: "rgba(168, 85, 247, 0.2)",
      borderColor: STRIPE_PRIMARY,
    },
    ".Error": {
      color: "#ef4444",
      fontSize: "15px",
    },
  },
};

// Exclusive Membership – gold / amber palette for Stripe Elements (dark theme)
const STRIPE_EXCLUSIVE_PRIMARY = "#f59e0b";
const STRIPE_EXCLUSIVE_PRIMARY_BRIGHT = "#fbbf24";
const STRIPE_EXCLUSIVE_BORDER_FOCUS = "rgba(245, 158, 11, 0.6)";
const STRIPE_EXCLUSIVE_TINT = "rgba(245, 158, 11, 0.12)";

/**
 * Stripe Appearance API – Exclusive Membership variant (gold accents on dark)
 * Used by FullscreenExclusive / overridePackId="exclusive" flow.
 */
export const STRIPE_APPEARANCE_EXCLUSIVE = {
  theme: "night" as const,
  variables: {
    colorPrimary: STRIPE_EXCLUSIVE_PRIMARY,
    colorBackground: STRIPE_BG,
    colorText: STRIPE_TEXT,
    colorDanger: "#ef4444",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif',
    spacingUnit: "6px",
    borderRadius: "12px",
    fontSizeBase: "16px",
    fontWeightNormal: "400",
    fontWeightMedium: "500",
    fontWeightBold: "600",
  },
  rules: {
    ".Block": {
      backgroundColor: STRIPE_BG_ELEVATED,
      border: `1px solid ${STRIPE_BORDER}`,
      borderRadius: "12px",
    },
    ".Input": {
      backgroundColor: STRIPE_BG_ELEVATED,
      border: `1px solid ${STRIPE_BORDER}`,
      color: STRIPE_TEXT,
      padding: "14px 16px",
      fontSize: "17px",
      boxShadow: "none",
      lineHeight: "1.4",
    },
    ".Input:focus": {
      border: `1px solid ${STRIPE_EXCLUSIVE_BORDER_FOCUS}`,
      boxShadow: `0 0 0 2px rgba(245, 158, 11, 0.2)`,
      outline: "none",
    },
    ".Input::placeholder": {
      color: STRIPE_TEXT_PLACEHOLDER,
    },
    ".Label": {
      color: STRIPE_TEXT_MUTED,
      fontSize: "15px",
      fontWeight: "500",
      marginBottom: "8px",
      letterSpacing: "-0.01em",
    },
    ".Tab": {
      backgroundColor: STRIPE_BG_ELEVATED,
      border: `1px solid ${STRIPE_BORDER}`,
      color: STRIPE_TEXT_MUTED,
      padding: "14px 16px",
      fontSize: "17px",
    },
    ".Tab:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.08)",
      color: STRIPE_TEXT,
    },
    ".Tab--selected": {
      backgroundColor: STRIPE_EXCLUSIVE_TINT,
      border: `1px solid ${STRIPE_EXCLUSIVE_PRIMARY}`,
      color: STRIPE_TEXT,
      fontWeight: "500",
      boxShadow: `0 0 0 1px ${STRIPE_EXCLUSIVE_PRIMARY}`,
    },
    ".PickerItem--selected": {
      backgroundColor: STRIPE_EXCLUSIVE_TINT,
      borderColor: STRIPE_EXCLUSIVE_PRIMARY_BRIGHT,
    },
    ".Error": {
      color: "#ef4444",
      fontSize: "15px",
    },
  },
};
